import { Box, Button, FormLabel, LinearProgress, Pagination, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ChangeEvent, useRef, useState, useEffect, useCallback } from 'react';

import FormError from 'components/Forms/FormError';
import { LoadingContent } from 'components/MyCustomers/ProductListMyCustomers/components/LoadingContent';
import { useServerTranslation } from 'hooks/useServerTranslation';
import { SearchResultList } from './SearchResultList';
import { useGetConsultantTextSearchQuery, useGetOrganisationGeolocationsQuery } from '../../../generated/graphql';
import useMarket from '../../../stores/useMarket';
import { ConsultantMap, ConsultantMapHandle } from './ConsultantMap';

type GeocodeResult = { lat: number; lng: number };

// Use these specific types, reject too general types like 'country' or 'administrative_area_level_1' to enable name search
const SPECIFIC_LOCATION_TYPES = new Set([
    'street_address',
    'route',
    'postal_code',
    'locality',
    'sublocality',
    'sublocality_level_1',
    'sublocality_level_2',
    'neighborhood',
    'administrative_area_level_2',
    'administrative_area_level_3',
]);

async function geocodeAddress(address: string, apiKey: string, countryCode: string): Promise<GeocodeResult> {
    const params = new URLSearchParams({
        address,
        components: `country:${countryCode}`,
        key: apiKey,
    });
    const url = `https://maps.googleapis.com/maps/api/geocode/json?${params}`;
    const response = await fetch(url);
    const data: unknown = await response.json();
    if (
        typeof data === 'object'
        && data !== null
        && (data as any).status === 'OK'
        && Array.isArray((data as any).results)
        && (data as any).results.length > 0
        && (data as any).results[0].geometry
        && (data as any).results[0].geometry.location
    ) {
        const resultTypes: string[] = (data as any).results[0].types || [];
        if (!resultTypes.some((t) => SPECIFIC_LOCATION_TYPES.has(t))) {
            throw new Error('Geocoding result too broad');
        }
        const { lat, lng } = (data as any).results[0].geometry.location;

        return { lat, lng };
    }
    throw new Error('Geocoding failed');
}

function haversine(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
    const toRad = (x: number) => (x * Math.PI) / 180;
    const R = 6371; // Earth radius in km
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const d = Math.sin(dLat / 2) * Math.sin(dLat / 2)
        + Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(d), Math.sqrt(1 - d));

    return R * c;
}

type FindConsultantProps = {
    hideDefaultHeader?: boolean;
};

export function FindConsultant({ hideDefaultHeader }: FindConsultantProps = {}) {
    const { t } = useServerTranslation();
    const ref = useRef<HTMLInputElement>(null);
    const [page, setPage] = useState<number>(1);
    const [query, setQuery] = useState<string>('');
    const [clickedOrganisationId, setClickedOrganisationId] = useState<number | undefined>(undefined);
    const { market, googleMapsCountryCode } = useMarket();
    const [visibleConsultants, setVisibleConsultants] = useState<Array<{ id: number; distance?: number }>>([]);
    const [searchCenter, setSearchCenter] = useState<{ lat: number; lng: number } | null>(null);
    const [fallbackConsultantIds, setFallbackConsultantIds] = useState<number[]>([]);
    const [fallbackDistanceMap, setFallbackDistanceMap] = useState<Record<number, number>>({});
    const [fallbackMessage, setFallbackMessage] = useState<string | null>(null);
    const [isUserInteracting, setIsUserInteracting] = useState<boolean>(false);
    const isUserInteractingRef = useRef<boolean>(false);
    const hasUserMovedMapRef = useRef<boolean>(false);
    const mapRef = useRef<ConsultantMapHandle>(null);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const { data: geoData, previousData: geoPrev, loading: geoLoading } = useGetOrganisationGeolocationsQuery({ variables: { market } });

    const {
        loading,
        error,
        data,
        previousData,
    } = useGetConsultantTextSearchQuery({
        variables: {
            market,
            query: searchCenter ? '' : query,
            page,
            limit: searchCenter ? 100 : 10,
            organisationIds: searchCenter && visibleConsultants.length > 0 ? visibleConsultants.map((c) => c.id) : undefined,
        },
        skip: fallbackConsultantIds.length > 0 || (!query && !searchCenter),
    });

    const result = data?.organisationPublicSearch || previousData?.organisationPublicSearch;

    const textSearchResultIds = result?.items?.map((item) => item.id) || [];

    const onMapMarkerClicked = useCallback((organisationId: number) => {
        setClickedOrganisationId(organisationId);
        setPage(1);
        window.scrollTo({ top: 200, behavior: 'smooth' });
    }, []);

    const onVisibleConsultantsChange = useCallback((consultants: Array<{ id: number; distance?: number }>) => {
        setVisibleConsultants(consultants);
    }, []);

    const onUserInteraction = useCallback((interacting: boolean) => {
        isUserInteractingRef.current = interacting;
        setIsUserInteracting(interacting);

        if (interacting) {
            hasUserMovedMapRef.current = true;
        }
    }, []);

    // Fallback logic: if no results, try to find nearest consultant(s)
    useEffect(() => {
        // Only trigger fallback for pure text searches (no searchCenter) that return no results
        if (result && typeof result.total === 'number' && result.total === 0 && query && geoData?.organisationGeolocations?.items && fallbackConsultantIds.length === 0 && !loading && !searchCenter) {
            (async () => {
                const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!;
                try {
                    const coords = await geocodeAddress(query, apiKey, googleMapsCountryCode);
                    const allGeolocations = geoData.organisationGeolocations.items;

                    const withDistance = allGeolocations.map((c: any) => ({
                        ...c,
                        distance: haversine(coords, { lat: c.latitude, lng: c.longitude }),
                    }));
                    const sorted = withDistance.sort((a, b) => a.distance - b.distance);
                    const nearest = sorted.filter((c) => c.distance < 50);
                    if (nearest.length > 0) {
                        setFallbackConsultantIds(nearest.map((c) => c.organisation_id));
                        setFallbackDistanceMap(Object.fromEntries(nearest.map((c) => [c.organisation_id, c.distance])));
                        setSearchCenter(coords);
                        setFallbackMessage(
                            t('find-consultant.fallback-message', { search_query: query })
                            || `Hittade ingen konsult för '${query}'. Visar de närmaste konsulterna istället.`,
                        );
                        // Fit to the nearest consultants to make sure markers and list align after switching searches
                        try {
                            const bounds = new google.maps.LatLngBounds();
                            nearest.forEach((n: any) => bounds.extend(new google.maps.LatLng(n.latitude, n.longitude)));
                            mapRef.current?.fitBounds(bounds);
                        } catch {
                            mapRef.current?.panTo(coords.lat, coords.lng, 13);
                        }
                    } else {
                        setFallbackConsultantIds([]);
                        setFallbackDistanceMap({});
                        setFallbackMessage(
                            t('find-consultant.no-nearby')
                            || 'No consultant found and no nearby consultants within 50km.',
                        );
                    }
                } catch (e) {
                    setFallbackConsultantIds([]);
                    setFallbackDistanceMap({});
                    setFallbackMessage(
                        t('find-consultant.geocode-error')
                        || 'No consultant found and location could not be determined.',
                    );
                }
            })();
        }
    }, [result, query, geoData, t, fallbackConsultantIds.length, loading, searchCenter, googleMapsCountryCode]);

    useEffect(() => {
        if (result && typeof result.total === 'number' && result.total > 0 && query && fallbackConsultantIds.length === 0) {
            if (searchCenter && !isUserInteractingRef.current && !hasUserMovedMapRef.current) {
                (async () => {
                    const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!;
                    try {
                        const coords = await geocodeAddress(query, apiKey, googleMapsCountryCode);
                        if (Math.abs(coords.lat - searchCenter.lat) < 0.001 && Math.abs(coords.lng - searchCenter.lng) < 0.001) {
                            mapRef.current?.panTo(coords.lat, coords.lng, 11);
                        }
                    } catch (geocodeError) {
                        // Ignore geocode errors
                    }
                })();
            } else if (!searchCenter && !isUserInteractingRef.current && !hasUserMovedMapRef.current) {
                mapRef.current?.fitBoundsToMarkers();
            }
        }
    }, [result, query, searchCenter, fallbackConsultantIds.length, googleMapsCountryCode]);

    // fetch full consultant data for the fallback consultants
    const {
        data: fallbackData,
        loading: fallbackLoading,
        error: fallbackError,
    } = useGetConsultantTextSearchQuery({
        variables: {
            market,
            query: '',
            organisationIds: fallbackConsultantIds.length > 0 ? fallbackConsultantIds : undefined,
            page: 1,
        },
        skip: fallbackConsultantIds.length === 0,
    });

    // fetch data for clicked consultant
    const {
        data: clickedData,
        loading: clickedLoading,
        error: clickedError,
    } = useGetConsultantTextSearchQuery({
        variables: {
            market,
            query: '',
            organisationIds: clickedOrganisationId,
            page: 1,
        },
        skip: !clickedOrganisationId,
    });

    const clickedResult = clickedData && clickedData.organisationPublicSearch.items.length > 0
        ? clickedData.organisationPublicSearch.items[0]
        : undefined;

    const paginationProps = {
        count: result?.pageCount || 1,
        page: result?.currentPage || 1,
        disabled: (result?.pageCount || 0) <= 0,
        onChange: async (event: ChangeEvent<unknown>, targetPage: number) => setPage(targetPage),
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const searchQuery = ref.current?.value || '';
        setQuery(searchQuery);
        setClickedOrganisationId(undefined);
        setIsUserInteracting(false);
        isUserInteractingRef.current = false;
        hasUserMovedMapRef.current = false;

        setFallbackConsultantIds([]);
        setFallbackDistanceMap({});
        setFallbackMessage(null);

        if (!searchQuery.trim()) {
            setSearchCenter(null);
            setQuery('');

            return;
        }

        // Always try geocoding first for any search
        try {
            const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!;
            const coords = await geocodeAddress(searchQuery, apiKey, googleMapsCountryCode);
            setSearchCenter(coords);
            if (isSmallScreen) {
                mapRef.current?.panTo(coords.lat, coords.lng, 10);
            } else {
                mapRef.current?.panTo(coords.lat, coords.lng, 11);
            }
        } catch (geocodeError) {
            // If geocoding fails, treat as name search
            setSearchCenter(null);
        }
    };

    useEffect(() => {
        (window as any).__consultantMapFocus = (organisationId: number) => mapRef.current?.focusConsultant(organisationId);

        return () => {
            delete (window as any).__consultantMapFocus;
        };
    }, []);

    return (
        <>
            <Box sx={{ '*': { display: 'block' } }}>
                {!hideDefaultHeader && (
                    <>
                        <Typography variant="h1">{t('find-consultant.title')}</Typography>
                        <Typography className="description">{t('find-consultant.text')}</Typography>
                    </>
                )}
                <ConsultantMap
                    ref={mapRef}
                    onMapMarkerClicked={onMapMarkerClicked}
                    onVisibleConsultantsChange={onVisibleConsultantsChange}
                    searchCenter={searchCenter}
                    fallbackConsultantIds={fallbackConsultantIds}
                    textSearchResultIds={textSearchResultIds}
                    textSearchResults={result?.items}
                    clickedResult={clickedResult}
                    onUserInteraction={onUserInteraction}
                />
                <form onSubmit={handleSubmit}>
                    <Stack gap={1}>
                        <FormLabel>{t('find-consultant.form-label')}</FormLabel>
                        <TextField
                            variant="outlined"
                            size="small"
                            inputRef={ref}
                        />
                        <Button
                            variant="contained"
                            type="submit"
                        >
                            {t('find-consultant.form-button')}
                        </Button>
                    </Stack>
                </form>
            </Box>
            {loading && <LinearProgress />}
            <FormError show={Boolean(error?.message)}>
                {error?.message}
            </FormError>
            <LoadingContent loading={loading || fallbackLoading}>
                {fallbackMessage && <Typography color="warning.main" sx={{ mt: 2, mb: 2 }}>{fallbackMessage}</Typography>}
                {fallbackConsultantIds.length > 0
                 && fallbackData?.organisationPublicSearch?.items
                 && fallbackData.organisationPublicSearch.items.length > 0
                    ? (
                        <SearchResultList
                            list={{
                                items: [...fallbackData.organisationPublicSearch.items]
                                    .sort((a, b) => (fallbackDistanceMap[a.id] ?? Infinity) - (fallbackDistanceMap[b.id] ?? Infinity)),
                                count: fallbackData.organisationPublicSearch.count ?? 0,
                                total: fallbackData.organisationPublicSearch.total ?? 0,
                                currentPage: 1,
                                pageCount: 1,
                            }}
                            distanceMap={fallbackDistanceMap}
                        />
                    )
                    : (
                        <SearchResultList
                            list={result ? {
                                ...result,
                                items: searchCenter
                                    ? [...(result.items || [])].sort((a, b) => {
                                        const distanceA = visibleConsultants.find((c) => c.id === a.id)?.distance || Infinity;
                                        const distanceB = visibleConsultants.find((c) => c.id === b.id)?.distance || Infinity;

                                        return distanceA - distanceB;
                                    })
                                    : result.items,
                            } : result}
                            distanceMap={searchCenter ? Object.fromEntries(visibleConsultants.map((c) => [c.id, c.distance || 0])) : undefined}
                        />
                    )}
            </LoadingContent>
            <Pagination
                sx={{ ul: { justifyContent: 'center' } }}
                color="primary"
                variant="text"
                shape="rounded"
                {...paginationProps}
            />
        </>
    );
}
